<template>
  <div class="project-case-detail-page">
    <!-- 头部导航 - 使用公共组件 -->
    <Navbar />

    <!-- 案例头部 -->
    <section class="case-header-section">
      <div class="container">
        <div class="case-meta">
          <el-breadcrumb separator-class="el-icon-arrow-right">
            <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item :to="{ path: '/project-cases' }">项目案例</el-breadcrumb-item>
            <el-breadcrumb-item>{{ projectCase.title }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        
        <div class="case-info">
          <h1 class="case-title">{{ projectCase.title }}</h1>
          <div class="case-meta-info">
            <div class="meta-item">
              <i class="el-icon-office-building"></i>
              <span>{{ projectCase.client }}</span>
            </div>
            <div class="meta-item">
              <i class="el-icon-date"></i>
              <span>{{ projectCase.completedDate }}</span>
            </div>
            <div class="meta-item">
              <i class="el-icon-time"></i>
              <span>{{ projectCase.duration }}</span>
            </div>
            <div class="meta-item">
              <i class="el-icon-folder-opened"></i>
              <span>{{ projectCase.category }}</span>
            </div>
            <div class="meta-item">
              <i class="el-icon-location-outline"></i>
              <span>{{ projectCase.location }}</span>
            </div>
          </div>
          <div class="case-tags" v-if="projectCase.tags && projectCase.tags.length">
            <span class="tag" v-for="tag in projectCase.tags" :key="tag">{{ tag }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 案例内容 -->
    <section class="case-content-section">
      <div class="container">
        <div class="content-wrapper">
          <!-- 左侧：案例正文 -->
          <article class="case-main">
            <!-- 案例封面图 -->
            <div class="case-image" v-if="projectCase.image">
              <img :src="projectCase.image" :alt="projectCase.title" />
            </div>

            <!-- 项目概述 -->
            <div class="case-overview" v-if="projectCase.overview">
              <h2 class="section-title">
                <i class="el-icon-document"></i>
                项目概述
              </h2>
              <p>{{ projectCase.overview }}</p>
            </div>

            <!-- 项目详情内容 -->
            <div class="case-content">
              <!-- 项目背景 -->
              <div class="content-section" v-if="projectCase.background">
                <h2 class="section-title">
                  <i class="el-icon-info"></i>
                  项目背景
                </h2>
                <div v-html="projectCase.background"></div>
              </div>

              <!-- 面临挑战 -->
              <div class="content-section" v-if="projectCase.challenges">
                <h2 class="section-title">
                  <i class="el-icon-warning-outline"></i>
                  面临挑战
                </h2>
                <div v-html="projectCase.challenges"></div>
              </div>

              <!-- 解决方案 -->
              <div class="content-section" v-if="projectCase.solution">
                <h2 class="section-title">
                  <i class="el-icon-setting"></i>
                  解决方案
                </h2>
                <div v-html="projectCase.solution"></div>
              </div>

              <!-- 实施过程 -->
              <div class="content-section" v-if="projectCase.implementation">
                <h2 class="section-title">
                  <i class="el-icon-sort"></i>
                  实施过程
                </h2>
                <div v-html="projectCase.implementation"></div>
              </div>

              <!-- 项目成果 -->
              <div class="content-section" v-if="projectCase.results">
                <h2 class="section-title">
                  <i class="el-icon-trophy"></i>
                  项目成果
                </h2>
                <div v-html="projectCase.results"></div>
              </div>

              <!-- 客户评价 -->
              <div class="content-section testimonial" v-if="projectCase.testimonial">
                <h2 class="section-title">
                  <i class="el-icon-chat-dot-round"></i>
                  客户评价
                </h2>
                <blockquote class="testimonial-content">
                  <p>{{ projectCase.testimonial.content }}</p>
                  <footer class="testimonial-author">
                    <strong>{{ projectCase.testimonial.author }}</strong>
                    <span class="author-title">{{ projectCase.testimonial.position }}</span>
                  </footer>
                </blockquote>
              </div>
            </div>

            <!-- 案例底部导航 -->
            <div class="case-footer">
              <div class="case-nav">
                <div class="nav-item prev" v-if="prevCase" @click="goToCase(prevCase.id)">
                  <span class="nav-label">上一个案例</span>
                  <span class="nav-title">{{ prevCase.title }}</span>
                </div>
                <div class="nav-item next" v-if="nextCase" @click="goToCase(nextCase.id)">
                  <span class="nav-label">下一个案例</span>
                  <span class="nav-title">{{ nextCase.title }}</span>
                </div>
              </div>
            </div>
          </article>

          <!-- 右侧：侧边栏 -->
          <aside class="case-sidebar">
            <!-- 项目信息卡片 -->
            <div class="sidebar-widget project-info-card">
              <h3 class="widget-title">项目信息</h3>
              <div class="project-details">
                <div class="detail-item">
                  <span class="detail-label">客户名称</span>
                  <span class="detail-value">{{ projectCase.client }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">项目类型</span>
                  <span class="detail-value">{{ projectCase.category }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">项目地点</span>
                  <span class="detail-value">{{ projectCase.location }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">完成时间</span>
                  <span class="detail-value">{{ projectCase.completedDate }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">项目周期</span>
                  <span class="detail-value">{{ projectCase.duration }}</span>
                </div>
                <div class="detail-item" v-if="projectCase.projectValue">
                  <span class="detail-label">项目价值</span>
                  <span class="detail-value">{{ projectCase.projectValue }}</span>
                </div>
              </div>
            </div>

            <!-- 相关案例推荐 -->
            <div class="sidebar-widget">
              <h3 class="widget-title">相关案例</h3>
              <div class="related-cases">
                <div 
                  v-for="relatedCase in relatedCases" 
                  :key="relatedCase.id"
                  class="related-item"
                  @click="goToCase(relatedCase.id)"
                >
                  <div class="related-image">
                    <img :src="relatedCase.image" :alt="relatedCase.title" />
                  </div>
                  <div class="related-content">
                    <h4 class="related-title">{{ relatedCase.title }}</h4>
                    <p class="related-meta">
                      <span>{{ relatedCase.client }}</span>
                      <span>{{ relatedCase.completedDate }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 服务类型标签 -->
            <div class="sidebar-widget">
              <h3 class="widget-title">服务类型</h3>
              <div class="service-tags">
                <span 
                  v-for="service in serviceTypes" 
                  :key="service"
                  class="service-tag"
                  @click="searchByService(service)"
                >
                  {{ service }}
                </span>
              </div>
            </div>

            <!-- 返回列表 -->
            <div class="sidebar-widget">
              <el-button type="primary" @click="backToList" size="large" style="width: 100%">
                <i class="el-icon-back"></i>
                返回案例列表
              </el-button>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <!-- 底部区域 - 使用公共组件 -->
    <Footer />
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'ProjectCaseDetail',
  components: {
    Navbar,
    Footer
  },
  data() {
    return {
      projectCase: {
        id: 1,
        title: 'XX石化公司空气呼吸器检测项目',
        client: 'XX石化有限公司',
        category: '空气呼吸器检测',
        location: '西安市经开区',
        completedDate: '2025年8月',
        duration: '15个工作日',
        projectValue: '50万元',
        overview: '为XX石化公司提供全厂区500余套空气呼吸器的专业检测服务，确保员工在危险环境下的安全防护，提升企业安全生产水平。',
        image: require('@/assets/news1.jpg'),
        background: `
          <p>XX石化公司作为大型石油化工企业，拥有多个生产装置和储罐区域，存在着多种有毒有害气体泄漏的风险。按照国家安全生产法规要求，企业必须为员工配备空气呼吸器等个人防护设备，并定期进行专业检测。</p>
          <p>该公司现有空气呼吸器500余套，分布在不同的工作岗位，设备使用频率较高，亟需进行全面的性能检测和维护保养。</p>
        `,
        challenges: `
          <ul>
            <li><strong>设备数量多、分布广</strong>：500余套设备分布在厂区各个角落，检测工作量大</li>
            <li><strong>工期紧迫</strong>：企业要求在不影响正常生产的情况下，尽快完成检测</li>
            <li><strong>标准要求高</strong>：必须严格按照GB/T 16556标准执行，确保检测质量</li>
            <li><strong>现场环境复杂</strong>：化工企业安全要求高，检测人员需要遵守严格的安全规定</li>
          </ul>
        `,
        solution: `
          <h3>检测方案设计</h3>
          <p>针对项目特点，我们制定了科学合理的检测方案：</p>
          <ol>
            <li><strong>分批检测</strong>：将500套设备分为5批，每批100套，分期进行检测</li>
            <li><strong>现场检测</strong>：派遣专业技术团队到现场进行检测，减少设备搬运风险</li>
            <li><strong>建档管理</strong>：为每台设备建立检测档案，便于后续管理和追溯</li>
            <li><strong>技术培训</strong>：为企业安全管理人员提供设备维护培训</li>
          </ol>
          
          <h3>检测设备配置</h3>
          <p>使用国际先进的检测设备：</p>
          <ul>
            <li>德国德尔格公司PTS 7000检测台</li>
            <li>高精度流量计和压力传感器</li>
            <li>专业的面罩密封性测试设备</li>
            <li>气瓶阀门性能测试装置</li>
          </ul>
        `,
        implementation: `
          <h3>第一阶段：前期准备（2个工作日）</h3>
          <ul>
            <li>现场勘察，了解设备分布情况</li>
            <li>制定详细的检测计划和安全措施</li>
            <li>协调企业相关部门，确定检测时间安排</li>
          </ul>

          <h3>第二阶段：现场检测（10个工作日）</h3>
          <ul>
            <li>按计划分批次进行设备检测</li>
            <li>每台设备进行全面的性能测试</li>
            <li>对发现的问题设备进行详细记录</li>
            <li>提供现场技术指导和维护建议</li>
          </ul>

          <h3>第三阶段：报告整理（3个工作日）</h3>
          <ul>
            <li>整理检测数据，编制检测报告</li>
            <li>制作设备维护建议书</li>
            <li>建立设备电子档案系统</li>
          </ul>
        `,
        results: `
          <h3>检测成果</h3>
          <ul>
            <li><strong>完成检测：</strong>成功检测空气呼吸器526台，检测合格率95.2%</li>
            <li><strong>发现问题：</strong>识别并处理25台存在安全隐患的设备</li>
            <li><strong>建立档案：</strong>为所有设备建立了完整的检测档案</li>
            <li><strong>提升管理：</strong>帮助企业建立了设备定期检测管理制度</li>
          </ul>

          <h3>项目效益</h3>
          <ul>
            <li><strong>安全保障：</strong>确保了员工个人防护设备的可靠性</li>
            <li><strong>合规达标：</strong>满足了国家安全生产法规要求</li>
            <li><strong>成本节约：</strong>通过及时维护，延长了设备使用寿命</li>
            <li><strong>管理提升：</strong>建立了科学的设备管理体系</li>
          </ul>

          <div class="results-stats">
            <div class="stat-item">
              <div class="stat-number">526</div>
              <div class="stat-label">检测设备数量</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">95.2%</div>
              <div class="stat-label">检测合格率</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">15</div>
              <div class="stat-label">实际完成天数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">100%</div>
              <div class="stat-label">客户满意度</div>
            </div>
          </div>
        `,
        testimonial: {
          content: '慧安检测团队专业高效，不仅按时完成了检测任务，还帮助我们建立了完善的设备管理制度。他们的专业建议对提升我们的安全管理水平非常有帮助。',
          author: '张总工程师',
          position: 'XX石化公司安全部'
        },
        tags: ['空气呼吸器', '石化行业', '大型项目', '现场检测', '档案管理']
      },
      prevCase: {
        id: 2,
        title: 'YY化工厂气体报警仪检测项目'
      },
      nextCase: {
        id: 3,
        title: 'ZZ电力公司气瓶检测项目'
      },
      relatedCases: [
        {
          id: 4,
          title: 'ABC制药公司呼吸器检测服务',
          client: 'ABC制药有限公司',
          image: require('@/assets/banner2.jpg'),
          completedDate: '2025年7月'
        },
        {
          id: 5,
          title: 'DEF钢铁厂安全设备检测项目',
          client: 'DEF钢铁集团',
          image: require('@/assets/banner3.jpg'),
          completedDate: '2025年6月'
        },
        {
          id: 6,
          title: 'GHI港口气体检测仪校准项目',
          client: 'GHI国际港务集团',
          image: require('@/assets/banner1.png'),
          completedDate: '2025年5月'
        }
      ],
      serviceTypes: ['空气呼吸器检测', '气体报警仪检测', '气瓶检测', '压力容器检测', '安全阀检测', '现场检测服务']
    }
  },
  methods: {
    // 获取案例数据
    fetchCase(id) {
      // TODO: 从API获取案例数据
      console.log('获取案例ID:', id)
      
      // 模拟根据ID获取不同案例
      const cases = {
        1: this.projectCase,
        2: {
          id: 2,
          title: 'YY化工厂气体报警仪检测项目',
          client: 'YY化工有限公司',
          category: '气体报警仪检测',
          location: '宝鸡高新区',
          completedDate: '2025年7月',
          duration: '8个工作日',
          overview: '为YY化工厂提供全厂区气体报警仪的检测和校准服务。',
          image: require('@/assets/news2.jpg'),
          background: '<p>YY化工厂气体报警仪检测项目背景...</p>',
          tags: ['气体报警仪', '化工行业', '校准服务']
        }
      }
      
      this.projectCase = cases[id] || this.projectCase
      this.updatePageTitle()
    },
    
    // 跳转到案例
    goToCase(id) {
      this.$router.push(`/project-cases/${id}`)
    },
    
    // 按服务类型搜索
    searchByService(service) {
      this.$router.push({ path: '/project-cases', query: { service } })
    },
    
    // 返回列表
    backToList() {
      this.$router.push('/project-cases')
    },
    
    // 更新页面标题
    updatePageTitle() {
      document.title = `${this.projectCase.title} - 慧安检测`
    }
  },
  
  mounted() {
    const caseId = this.$route.params.id
    this.fetchCase(caseId)
  },
  
  watch: {
    '$route'(to, from) {
      if (to.params.id !== from.params.id) {
        this.fetchCase(to.params.id)
      }
    }
  }
}
</script>

<style scoped>
.project-case-detail-page {
  min-height: 100vh;
  background: #ffffff;
}

/* 案例头部 */
.case-header-section {
  padding: 120px 0 40px;
  background: #f7f8fa;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.case-meta {
  margin-bottom: 32px;
}

.case-info {
  max-width: 800px;
}

.case-title {
  font-size: 36px;
  font-weight: 700;
  color: #333333;
  line-height: 1.4;
  margin: 0 0 24px 0;
}

.case-meta-info {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666666;
}

.meta-item i {
  font-size: 16px;
  color: #1e3a8a;
}

.case-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tag {
  padding: 4px 12px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  color: #1e3a8a;
  font-size: 12px;
  border-radius: 4px;
  font-weight: 500;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

/* 案例内容区域 */
.case-content-section {
  padding: 40px 0 80px;
  background: #ffffff;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 60px;
}

/* 案例正文 */
.case-main {
  background: white;
}

.case-image {
  margin-bottom: 32px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.case-image img {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.case-overview {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  padding: 24px;
  border-left: 4px solid #1e3a8a;
  margin-bottom: 40px;
  border-radius: 0 8px 8px 0;
}

.case-overview .section-title {
  margin: 0 0 16px 0;
  font-size: 20px;
  color: #333333;
}

.case-overview p {
  font-size: 16px;
  line-height: 1.7;
  color: #666666;
  margin: 0;
}

.case-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333333;
}

.content-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #333333;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #1e3a8a;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title i {
  color: #1e3a8a;
  font-size: 20px;
}

.case-content h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 24px 0 16px 0;
}

.case-content p {
  margin: 16px 0;
  text-align: justify;
}

.case-content ul,
.case-content ol {
  margin: 16px 0;
  padding-left: 32px;
}

.case-content li {
  margin: 8px 0;
}

.case-content li strong {
  color: #1e3a8a;
  font-weight: 600;
}

/* 客户评价 */
.testimonial .testimonial-content {
  background: #f8f9fa;
  border-left: 4px solid #1e3a8a;
  padding: 24px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;
  font-style: italic;
}

.testimonial-content p {
  font-size: 16px;
  line-height: 1.7;
  color: #333333;
  margin: 0 0 16px 0;
}

.testimonial-author {
  text-align: right;
  font-style: normal;
}

.testimonial-author strong {
  color: #333333;
  font-size: 16px;
}

.author-title {
  color: #999999;
  font-size: 14px;
  margin-left: 8px;
}

/* 项目成果统计 */
.results-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin: 32px 0;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #1e3a8a;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #666666;
  font-weight: 500;
}

/* 案例底部 */
.case-footer {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid #e8e8e8;
}

.case-nav {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.nav-item {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.nav-item:hover {
  background: #1e3a8a;
  color: white;
}

.nav-item.prev {
  text-align: left;
}

.nav-item.next {
  text-align: right;
}

.nav-label {
  font-size: 12px;
  color: #999999;
  display: block;
  margin-bottom: 4px;
}

.nav-item:hover .nav-label {
  color: rgba(255, 255, 255, 0.8);
}

.nav-title {
  font-size: 14px;
  font-weight: 500;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 侧边栏 */
.case-sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sidebar-widget {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.widget-title {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
  position: relative;
}

.widget-title::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 40px;
  height: 2px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
}

/* 项目信息卡片 */
.project-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 14px;
  color: #666666;
  font-weight: 500;
}

.detail-value {
  font-size: 14px;
  color: #333333;
  font-weight: 600;
}

/* 相关案例推荐 */
.related-cases {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.related-item {
  display: flex;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s;
  padding: 8px;
  border-radius: 8px;
}

.related-item:hover {
  background: #f8f9fa;
}

.related-image {
  width: 80px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;
}

.related-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-content {
  flex: 1;
  min-width: 0;
}

.related-title {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
  margin: 0 0 6px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-meta {
  font-size: 12px;
  color: #999999;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* 服务类型标签 */
.service-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.service-tag {
  padding: 6px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
  color: #666666;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.service-tag:hover {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  transform: translateY(-2px);
}

/* 响应式设计 */
@media (max-width: 992px) {
  .content-wrapper {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .case-sidebar {
    order: -1;
  }

  .case-title {
    font-size: 28px;
  }

  .case-meta-info {
    gap: 16px;
  }

  .case-nav {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .results-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .case-header-section {
    padding: 100px 0 30px;
  }

  .case-title {
    font-size: 24px;
  }

  .case-content {
    font-size: 15px;
  }

  .section-title {
    font-size: 20px;
  }

  .case-content h3 {
    font-size: 16px;
  }

  .case-image img {
    height: 250px;
  }

  .results-stats {
    grid-template-columns: 1fr;
  }

  .stat-number {
    font-size: 28px;
  }
}
</style>
